<template>
  <div class="privileges-list">
    <div v-for="(privilege, index) in privilegeOptions" :key="index"
        :class="{'highlight': privilege.selected}" class="group-list">
      <span class="main-section" @click="toggleSection(index)">
        {{privilege.group}} ({{privilege.enabledCount}}/{{privilege.total}})

        <span v-if="isDesktop()">
          <i class="fa fa-angle-left" v-if="privilege.selected" />
          <i class="fa fa-angle-right" v-else />
        </span>
        <span v-else>
          <i class="fa fa-angle-up" v-if="privilege.selected" />
          <i class="fa fa-angle-down" v-else />
        </span>
      </span>

      <span v-if="privilege.selected" class="sub-section">
        <span v-for="(data, dataIndex) in privilege.list" :key="dataIndex" class="data-section">
          <span>
            {{data.text}}
            <i class="fa fa-info-circle" v-if="isRoleManagement && data.tooltip">
              <md-tooltip md-direction="top">{{data.tooltip}}</md-tooltip>
            </i>
          </span>

          <md-switch v-model="data.enabled" v-if="isRoleManagement"
                    :disabled="disableData || data.disable"
                    @change="togglePrivilege(data, index)" />
          <i class="fa fa-check-circle" v-else-if="data.enabled" />
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'PrivilegeList',
  data () {
    return {
      isRoleManagement: false,
      privilegeOptions: {},
      privilegeList: []
    }
  },
  props: {
    selectedPrivileges: {
      type: Array,
      default: () => []
    },
    disableData: {
      type: Boolean,
      default: false
    },
    reset: Boolean,
    selfVaEnabled: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    selectedPrivileges (newVal, oldVal) {
      this.privilegeList = newVal

      if (!this.isRoleManagement || (this.$route.name === 'RoleDetails' && !oldVal.length)) {
        this.calculateEnabledPrivileges()
      }
    },
    privilegeList (newVal) {
      this.$emit('updateList', newVal)
    },
    reset () {
      this.privilegeList = this.selectedPrivileges
      this.calculateEnabledPrivileges()
    },
    selfVaEnabled () {
      if (this.selfVaEnabled) {
        let group = this.privilegeOptions.find(e => e.group === 'Virtual Accounts')
        group.list.push(
          {
            text: 'Manage Received - VA Transactions',
            value: 'MANAGE_VA_TXNS',
            tooltip: `'Virtual Account Details' & 'Received - VA Transactions' privileges are mandatory`,
            mandatory_values: ['VA_READ', 'RECEIVED_VA_TXNS']
          },
          {
            text: 'Initiate Bank Transfers - VA',
            value: 'INITIATE_VA_FUND_TRANSFERS',
            tooltip: `'Virtual Account Details' & 'Received - VA Transactions' privileges are mandatory`,
            mandatory_values: ['VA_READ', 'RECEIVED_VA_TXNS']
          }
        )
        this.calculateEnabledPrivileges()
      }
    }
  },
  computed: {
    ...mapGetters(['getterPgStatus'])
  },
  created () {
    this.isRoleManagement = ['RoleCreate', 'RoleDetails'].includes(this.$route.name)
    this.populatePrivilegeOptions()
    this.calculateEnabledPrivileges()
  },
  methods: {
    toggleSection (dataIndex) {
      this.privilegeOptions.map((data, index) => {
        data.selected = index === dataIndex ? !data.selected : false
        return data
      })

      this.privilegeOptions = this.privilegeOptions.slice()
    },
    togglePrivilege (data, groupIndex) {
      const pos = this.privilegeList.indexOf(data.value)
      if (pos >= 0) {
        this.privilegeList.splice(pos, 1)
        if (data.dependency_values) {
          this.removeDependencies(data, groupIndex)
        }
      } else {
        this.privilegeList.push(data.value)
        if (data.mandatory_values) {
          this.addDependencies(data, groupIndex)
        }
      }

      this.calculateEnabledPrivileges(groupIndex)
    },
    addDependencies (data, groupIndex) {
      for (let val of data.mandatory_values) {
        if (this.privilegeList.indexOf(val) === -1) {
          this.setDependencyValue(val, groupIndex, true)
          this.privilegeList.push(val)
        }
      }
    },
    removeDependencies (data, groupIndex) {
      for (let dependent of data.dependency_values) {
        const pos = this.privilegeList.indexOf(dependent)
        if (pos >= 0) {
          this.setDependencyValue(dependent, groupIndex, false)
          this.privilegeList.splice(pos, 1)
        }
      }
    },
    setDependencyValue (checkVal, groupIndex, bool) {
      this.privilegeOptions[groupIndex].list.map(elem => {
        if (elem.value === checkVal) {
          elem.enabled = bool
        }
        return elem
      })
    },
    calculateEnabledPrivileges (groupIndex) {
      if (groupIndex >= 0) {
        let privileges = this.privilegeOptions[groupIndex].list
        this.privilegeOptions[groupIndex] = {
          ...this.privilegeOptions[groupIndex],
          selected: true,
          enabledCount: privileges.filter(data => data.enabled).length
        }
      } else {
        this.privilegeOptions.map(privilege => {
          privilege.list.map(data => {
            data.enabled = this.privilegeList.includes(data.value)
            return data
          })
          privilege.total = privilege.list.length
          privilege.enabledCount = privilege.list.filter(data => data.enabled).length
          privilege.selected = false
          return privilege
        })
      }
      this.privilegeOptions = this.privilegeOptions.slice()
    },
    populatePrivilegeOptions () {
      this.privilegeOptions = [
        {
          group: 'Balance',
          list: [
            {
              text: 'Account Balance',
              value: 'ACCOUNT_BALANCE'
            }
          ]
        },
        {
          group: 'Account',
          list: [
            {
              text: 'Account Details',
              value: 'ACCOUNT_DETAILS',
              dependency_values: ['FUNDS_ADDED_TXNS', 'LIEN_TXNS']
            },
            {
              text: 'Funds Added Transactions',
              value: 'FUNDS_ADDED_TXNS',
              tooltip: `'Account Details' privilege is mandatory`,
              mandatory_values: ['ACCOUNT_DETAILS']
            },
            {
              text: 'Lien Transactions',
              value: 'LIEN_TXNS',
              tooltip: `'Account Details' privilege is mandatory`,
              mandatory_values: ['ACCOUNT_DETAILS']
            }
          ]
        },
        {
          group: 'Fund Transfers',
          list: [
            {
              text: 'Bank Transfer Transactions',
              value: 'FUND_TRANSFER_TXNS',
              dependency_values: ['INITIATE_FUND_TRANSFERS', 'INITIATE_BULK_FUND_TRANSFERS']
            },
            {
              text: 'Initiate Bank Transfers',
              value: 'INITIATE_FUND_TRANSFERS',
              tooltip: `'Bank Transfer Transactions' privilege is mandatory`,
              mandatory_values: ['FUND_TRANSFER_TXNS']
            },
            {
              text: 'Initiate Bulk Bank Transfers',
              value: 'INITIATE_BULK_FUND_TRANSFERS',
              tooltip: `'Bank Transfer Transactions' privilege is mandatory`,
              mandatory_values: ['FUND_TRANSFER_TXNS']
            }
          ]
        },
        {
          group: 'Virtual Accounts',
          list: [
            {
              text: 'Virtual Account Details',
              value: 'VA_READ',
              dependency_values: ['VA_WRITE', 'RECEIVED_VA_TXNS', 'MANAGE_VA_TXNS', 'VA_BALANCE', 'INITIATE_VA_FUND_TRANSFERS']
            },
            {
              text: 'Virtual Account Balance',
              value: 'VA_BALANCE',
              tooltip: `'Virtual Account Details' privileges is mandatory`,
              mandatory_values: ['VA_READ']
            },
            {
              text: 'Manage Virtual Account',
              value: 'VA_WRITE',
              tooltip: `'Virtual Account Details' privilege is mandatory`,
              mandatory_values: ['VA_READ']
            },
            {
              text: 'Received - VA Transactions',
              value: 'RECEIVED_VA_TXNS',
              tooltip: `'Virtual Account Details' privilege is mandatory`,
              mandatory_values: ['VA_READ'],
              dependency_values: ['MANAGE_VA_TXNS', 'INITIATE_VA_FUND_TRANSFERS']
            }
          ]
        },
        {
          group: 'UPI',
          list: [
            {
              text: 'UPI Details',
              value: 'UPI_READ',
              dependency_values: ['UPI_WRITE', 'RECEIVED_UPI_TXNS']
            },
            {
              text: 'Manage UPI',
              value: 'UPI_WRITE',
              tooltip: `'UPI Details' privilege is mandatory`,
              mandatory_values: ['UPI_READ']
            },
            {
              text: 'Received - UPI Transactions',
              value: 'RECEIVED_UPI_TXNS',
              tooltip: `'UPI Details' privilege is mandatory`,
              mandatory_values: ['UPI_READ']
            }
          ]
        },
        {
          group: 'UPI Collections',
          list: [
            {
              text: 'UPI Collection Details',
              value: 'UPI_COLLECTIONS_READ',
              dependency_values: ['UPI_COLLECTIONS_WRITE', 'UPI_COLLECTION_TXNS', 'MANAGE_UC_TXNS']
            },
            {
              text: 'Manage UPI Collections',
              value: 'UPI_COLLECTIONS_WRITE',
              tooltip: `'UPI Collection Details' privilege is mandatory`,
              mandatory_values: ['UPI_COLLECTIONS_READ']
            },
            {
              text: 'UPI Collection Transactions',
              value: 'UPI_COLLECTION_TXNS',
              tooltip: `'UPI Collection Details' privilege is mandatory`,
              mandatory_values: ['UPI_COLLECTIONS_READ'],
              dependency_values: ['MANAGE_UC_TXNS']
            },
            {
              text: 'Manage UPI Collection Transactions',
              value: 'MANAGE_UC_TXNS',
              tooltip: `'UPI Collection Details' & 'UPI Collection Transactions' privileges are mandatory`,
              mandatory_values: ['UPI_COLLECTIONS_READ', 'UPI_COLLECTION_TXNS']
            }
          ]
        },
        {
          group: 'Bank & UPI Verification',
          list: [
            {
              text: 'Verify Bank Account Transactions',
              value: 'BANK_VERIFICATION_TXNS',
              dependency_values: ['INITIATE_BANK_VERIFICATIONS', 'INITIATE_BULK_BANK_VERIFICATIONS']
            },
            {
              text: 'Initiate Bank Verifications',
              value: 'INITIATE_BANK_VERIFICATIONS',
              tooltip: `'Verify Bank Account Transactions' privilege is mandatory`,
              mandatory_values: ['BANK_VERIFICATION_TXNS']
            },
            {
              text: 'Initiate Bulk Bank Verifications',
              value: 'INITIATE_BULK_BANK_VERIFICATIONS',
              tooltip: `'Verify Bank Account Transactions' privilege is mandatory`,
              mandatory_values: ['BANK_VERIFICATION_TXNS']
            },
            {
              text: 'Verify UPI Transactions',
              value: 'UPI_VERIFICATION_TXNS',
              dependency_values: ['INITIATE_UPI_VERIFICATIONS']
            },
            {
              text: 'Initiate UPI Verifications',
              value: 'INITIATE_UPI_VERIFICATIONS',
              tooltip: `'Verify UPI Transactions' privilege is mandatory`,
              mandatory_values: ['UPI_VERIFICATION_TXNS']
            }
          ]
        },
        {
          group: 'Invoices',
          list: [
            {
              text: 'Invoices',
              value: 'INVOICES',
              dependency_values: ['INVOICE_TXNS']
            },
            {
              text: 'Invoice Transactions',
              value: 'INVOICE_TXNS',
              tooltip: `'Invoices' privilege is mandatory`,
              mandatory_values: ['INVOICES']
            }
          ]
        },
        {
          group: 'Settings',
          list: [
            {
              text: 'Account Settings',
              value: 'ACCOUNT_SETTINGS'
            },
            {
              text: 'API Settings',
              value: 'API_SETTINGS'
            },
            {
              text: 'Webhook Settings',
              value: 'WEBHOOKS'
            }
          ]
        },
        {
          group: 'Roles & Users',
          list: [
            {
              text: 'Role Details',
              value: 'ROLES_READ',
              dependency_values: ['USERS_READ', 'USERS_WRITE']
            },
            {
              text: 'Manage Roles',
              value: 'ROLES_WRITE',
              tooltip: `'Manage Roles' privilege is for Admin only`,
              disable: true
            },
            {
              text: 'User Details',
              value: 'USERS_READ',
              dependency_values: ['USERS_WRITE'],
              tooltip: `'Role Details' privilege is mandatory`,
              mandatory_values: ['ROLES_READ']
            },
            {
              text: 'Manage Users',
              value: 'USERS_WRITE',
              tooltip: `'User Details' privilege is mandatory`,
              mandatory_values: ['USERS_READ', 'ROLES_READ']
            }
          ]
        }
      ]

      if (this.getterPgStatus === 'active') {
        this.privilegeOptions.push({
          group: 'Payment Gateway',
          list: [
            {
              text: 'Order Details',
              value: 'PG_ORDERS_READ',
              dependency_values: ['PG_ORDERS_WRITE', 'PG_REFUNDS_READ', 'PG_REFUNDS_WRITE']
            },
            {
              text: 'Manage Orders',
              value: 'PG_ORDERS_WRITE',
              tooltip: `'Order Details' privilege is mandatory`,
              mandatory_values: ['PG_ORDERS_READ']
            },
            {
              text: 'Refund Details',
              value: 'PG_REFUNDS_READ',
              dependency_values: ['PG_REFUNDS_WRITE'],
              tooltip: `'Order Details' privilege is mandatory`,
              mandatory_values: ['PG_ORDERS_READ']
            },
            {
              text: 'Manage Refunds',
              value: 'PG_REFUNDS_WRITE',
              tooltip: `'Order Details & Refund Details' privileges are mandatory`,
              mandatory_values: ['PG_ORDERS_READ', 'PG_REFUNDS_READ']
            }
          ]
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  .privileges-list {
    margin-top: 5px;
    position: relative;

    .main-section {
      @include custom-flex(space-between);
      padding: 10px;
      border: 1px solid $grey-outline;
      cursor: pointer;
    }
    .sub-section {
      display: flex;
      flex-direction: column;
      border: 1px solid $grey-outline;

      .data-section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px;
        border: 1px solid $grey-outline;

        span {
          text-align: left;
        }

        .fa {
          color: $primary;
          margin-left: 5px;
          cursor: pointer;
        }
      }
    }
  }

  .desktop {
    .highlight {
      .main-section {
        border: 2px solid $primary !important;
      }

      i {
        color: $primary;
      }
    }

    .main-section {
      width: 45%;
      background: $white;
      @include hover-lit;
    }

    .sub-section {
      position: absolute;
      right: 0;
      top: 0;
      padding: 0;
      width: 45%;
      padding: 0;
    }
  }

  .mobile, .tablet {
    .group-list {
      max-width: 350px;

      &.highlight {
        background: $grey-outline;
      }

      .sub-section {
        padding: 7px;

        .data-section {
          background: $white;
        }
      }
    }
  }
</style>
